import { useState, useEffect } from "react";
import { VictoryBar, VictoryChart, VictoryAxis, VictoryStack, VictoryTooltip } from "victory";
import { addDays, format, startOfWeek, endOfWeek } from "date-fns";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";

export default function TaskGraph({date}) {
  const today = new Date().toISOString().split("T")[0];
  const yesterday = addDays(new Date(), -1).toISOString().split("T")[0];

  const user = useContext(AuthContext);

  const [fromDate, setFromDate] = useState(today);
  const [tasks, setTasks] = useState([]);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);


  // set the from date to the start of the week and the to date to the end of the week
   





  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await fetch("https://api.insightos.co.uk/api/taskgraph", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
          body: JSON.stringify({ fromDate, toDate }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Task Graph Data:", result); // Debugging

    setTasks(result.data);
        // Assuming tasks are returned in result.data as an array
        const tasks = Array.isArray(result.data) ? result.data : [];

        // Assuming reports are returned either in result.reports or in result.data.reports
        const fetchedReports = Array.isArray(result.reports)
          ? result.reports
          : result.data && Array.isArray(result.data.reports)
          ? result.data.reports
          : [];
        setReports(fetchedReports);

        // Remove "Break" tasks
        const filteredTasks = tasks.filter((task) => task.Task !== "Break");

        // Group tasks by StaffName
        
        const graphDataMap = filteredTasks.reduce((acc, task) => {

            let name = task.Role || "Unknown";

if (user.access === 'admin') {
          const name = task.Role || "Unknown";
} else {
            const name = task.Name || "Unknown";
}
           // Default to "Unknown" if Name is missing
          if (!acc[name]) {
            acc[name] = { Name: name, Completed: 0, Incomplete: 0, IncompleteTasks: [] };
          }
          if (task.Completed === 1) {
            acc[name].Completed += 1;
          } else {
            acc[name].Incomplete += 1;
            acc[name].IncompleteTasks.push(task.Task || "Unnamed Task"); // Handle missing Task name
          }
          return acc;
        }, {});

        // Ensure all fields are valid numbers
        const validatedGraphData = Object.values(graphDataMap).map((entry) => ({
          ...entry,
          Completed: Number(entry.Completed) || 0,
          Incomplete: Number(entry.Incomplete) || 0,
          // get the task names from the IncompleteTasks array and tally them
          
        }));

        setGraphData(validatedGraphData);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
      setLoading(false);
    };

    getData();
  }, [fromDate, toDate]);

  // Regex replacement to ensure each ' --- ' has its own line
  const formatReportContent = (content) => {
    return content.replace(/\s*---\s*/g, "\n---\n");
  };

  // When a report is clicked, format its content
  const handleReportClick = (reportContent) => {
    const formattedContent = formatReportContent(reportContent);
    setSelectedReport(formattedContent);
  };

  const taskTally = (tasks) => {
    // Create a map where each key is a Role and its value is an object that
    // contains a nested tasks object (grouped by task name)
    const roleMap = tasks.reduce((acc, task) => {
      const role = task.Role || "Unknown";
      const taskName = task.Task || "Unnamed Task";

      // if Task is "Break", skip it

        if (taskName === "Break") {
            return acc;
        }

  
      // Initialize the role entry if it doesn't exist
      if (!acc[role]) {
        acc[role] = { Role: role, tasks: {} };
      }
  
      // Initialize the task entry if it doesn't exist for that role
      if (!acc[role].tasks[taskName]) {
        acc[role].tasks[taskName] = { Task: taskName, Completed: 0, Incomplete: 0 };
      }
  
      // Increment counts based on Completed value
      if (task.Completed === 1) {
        acc[role].tasks[taskName].Completed += 1;
      } else {
        acc[role].tasks[taskName].Incomplete += 1;
      }
  
      return acc;
    }, {});
  
    // Transform the roleMap into an array of roles with an array of tasks
    const result = Object.values(roleMap).map((roleEntry) => ({
      Role: roleEntry.Role,
      tasks: Object.values(roleEntry.tasks)
    }));
  
    return result;
  };
  



  return (
    <div className="p-6 bg-gray-900 rounded-lg shadow-lg w-full text-white" data-component="task-graph-dashboard">
      <h1 className="text-3xl font-bold mb-4 text-center text-cyan-400">Task Completion Graph</h1>

      {/* Date Inputs */}
      <div className="flex justify-center space-x-6 mb-6">
        <div className="flex flex-col">
          <label htmlFor="fromDate" className="text-cyan-300 text-sm mb-1">From:</label>
          <input
            type="date"
            id="fromDate"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-component="task-graph-from"
            className="bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="toDate" className="text-cyan-300 text-sm mb-1">To:</label>
          <input
            type="date"
            id="toDate"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-component="task-graph-to"
            className="bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
          />
        </div>
      </div>

    {/* Graph */}
        <div className="flex justify-center">
          {loading ? (
            <p className="text-cyan-300">Loading graph...</p>
          ) : graphData.length === 0 ? (
            <p className="text-cyan-300">No data available for the selected dates.</p>
          ) : (
            <VictoryChart domainPadding={20}>
            <VictoryAxis
              style={{
                tickLabels: { fill: "#A0F0FF", fontSize: 12, fontWeight: "bold" },
                axis: { stroke: "#A0F0FF" },
                grid: { stroke: "#444", strokeDasharray: "4" }
              }}
              tickFormat={(_, i) => graphData[i] ? graphData[i].Name : ''}
            />
            <VictoryAxis
              dependentAxis
              style={{
                tickLabels: { fill: "#A0F0FF", fontSize: 12 },
                axis: { stroke: "#A0F0FF" },
                grid: { stroke: "#444", strokeDasharray: "4" }
              }}
            />
            <VictoryStack>
              <VictoryBar
                data={graphData}
                x="Name"
                y="Completed"
                style={{
                data: { 
                  fill: "rgba(16, 185, 129, 0.4)",
                  filter: "drop-shadow(0px 4px 10px rgba(16, 185, 129, 0.6))",
                  stroke: "#ffffff",
                    strokeWidth: 1.5
                  }
                }}
                labels={({ datum }) => `✔ Completed: ${datum.Completed}`}
                labelComponent={<VictoryTooltip style={{ fill: "#03bc4a", fontSize: 12 }} />}
              />

              <VictoryBar
                data={graphData}
                x="Name"
                y="Incomplete"
                style={{
                  data: { 
                    fill: "rgba(255, 59, 59, 0.4)",
                    filter: "drop-shadow(0px 4px 10px rgba(255, 59, 59, 0.6))",
                    stroke: "#ffffff",
                    strokeWidth: 1.5
                  }
                }}
                labels={({ datum }) =>
                  datum.IncompleteTasks.length > 0
                    ? `❌ Incomplete: ${datum.Incomplete}`
                    : `❌ Incomplete: ${datum.Incomplete}`
                }
                labelComponent={
                  <VictoryTooltip 
                    flyoutStyle={{ fill: "#000000", stroke: "#FFF", strokeWidth: 1 }}
                    style={{ fill: "#FFF", fontSize: 12 }}
                    constrainToVisibleArea
                  />
                }
              />
            </VictoryStack>
          </VictoryChart>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-cyan-300 text-xl mb-4">Task Tally</h2>
     
        <div className="grid grid-cols-2 gap-4">
  {taskTally(tasks).map((role) => (
    <div key={role.Role} className="bg-gray-800 p-4 rounded-lg shadow-md">
      <h3 className="text-white text-sm font-semibold mb-2">{role.Role}</h3>
      <ul className="text-gray-300">
        {role.tasks.map((task) => (
          <li key={task.Task} className="mb-4 border-b border-gray-700 pb-2">
            <span className="font-bold text-xs text-cyan-400">{task.Task}</span>
            <div className="mt-1 text-xs">
              <span className="block text-green-400">✔ Completed: {task.Completed}</span>
              <span className="block text-red-400">✘ Incomplete: {task.Incomplete}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  ))}
</div>



        
        </div>

      {/* Report Buttons */}
      {reports.length > 0 && (
        <div className="mt-6">
          <h2 className="text-cyan-300 text-xl mb-4">Reports</h2>
          <div className="flex space-x-4">
             {reports.map((report) => (
                <button
                  key={report.ReportDate}
                  className="bg-cyan-500 px-4 py-2 rounded-lg text-white"
                  onClick={() => handleReportClick(report.Content)}
                  data-feature="open-task-report"
                >
                  {format(new Date(report.ReportDate), "dd/MM/yyyy")} - {report.DailyReport === 1 ? "Daily" : "Company"}
                </button>
              ))}
              
          </div>
        </div>
      )}

      {/* Selected Report Content */}
      {selectedReport && (
        <div className="mt-4 bg-gray-800 p-4 rounded-lg">
          <h3 className="text-xl text-cyan-300">Report Content:</h3>
          <p className="text-white">{selectedReport}</p>
        </div>
      )}
    </div>
  );
}
